import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Clock, X, type LucideIcon } from 'lucide-react';
import type { KycStatus } from '../api.js';

interface KycStatusBannerProps {
  status: KycStatus;
  /** If true, render a compact inline chip instead of a full card. */
  compact?: boolean;
}

const STATUS_STYLE: Record<KycStatus, { bg: string; border: string; Icon: LucideIcon; textColor: string }> = {
  unsubmitted: {
    bg: 'bg-[var(--warning-bg)]',
    border: 'border-[var(--warning)]/30',
    Icon: AlertTriangle,
    textColor: 'text-[var(--warning)]',
  },
  pending: {
    bg: 'bg-[var(--info-bg)]',
    border: 'border-[var(--info)]/30',
    Icon: Clock,
    textColor: 'text-[var(--info)]',
  },
  approved: {
    bg: 'bg-[var(--success-bg)]',
    border: 'border-[var(--success)]/30',
    Icon: Check,
    textColor: 'text-[var(--success)]',
  },
  rejected: {
    bg: 'bg-[var(--danger-bg)]',
    border: 'border-[var(--danger)]/30',
    Icon: X,
    textColor: 'text-[var(--danger)]',
  },
};

export function KycStatusBanner({ status, compact = false }: KycStatusBannerProps) {
  const { t } = useTranslation('kyc');
  const style = STATUS_STYLE[status];

  if (compact) {
    return (
      <span
        className={[
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold',
          style.bg,
          style.textColor,
        ].join(' ')}
      >
        <style.Icon className="size-3" aria-hidden />
        {t(`status.${status}`)}
      </span>
    );
  }

  return (
    <div
      className={[
        'rounded-xl border p-4 flex items-start gap-3',
        style.bg,
        style.border,
      ].join(' ')}
    >
      <style.Icon className={['size-5 mt-0.5 shrink-0', style.textColor].join(' ')} aria-hidden />
      <div className="flex-1 min-w-0">
        <p className={['text-[13px] font-semibold', style.textColor].join(' ')}>
          {t(`status.${status}`)}
        </p>
        {status === 'unsubmitted' && (
          <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">
            {t('profile.notVerifiedBanner')}
          </p>
        )}
      </div>
      {status === 'unsubmitted' && (
        <Link
          to="/kyc"
          className="shrink-0 text-[12px] font-semibold text-[var(--warning)] underline underline-offset-2"
        >
          {t('profile.verificationSection')} →
        </Link>
      )}
      {status === 'rejected' && (
        <Link
          to="/kyc"
          className="shrink-0 text-[12px] font-semibold text-[var(--danger)] underline underline-offset-2"
        >
          {t('rejection.retry')} →
        </Link>
      )}
    </div>
  );
}
